import React, { FC, useCallback } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { makeStyles } from "@material-ui/core";
import useUserQueries from "../../../../user/hooks/useUserQueries";
import { useNotify } from "../../../../notify/useNotify";
import { IFile } from "../../../../../components/UploadFile/types";
import { mutateErrors } from "../../../../../utils";
import { NotifyMode } from "../../../../notify/NotifyModeEnum";
import UploadFile from "../../../../../components/UploadFile/UploadFile";
import BootstrapInput from "../../../../../components/BootstrapInput";
import FieldsGroupLabel from "../common/FieldsGroupLabel";
import { ICourseComponentProps } from "../../../types";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
  },
  subContainer: {
    "& + &": {
      marginTop: 16,
    },
  },
}));

const ApplicationFiles: FC<ICourseComponentProps> = ({ fieldsGroupName }) => {
  const classes = useStyles();

  const { values } = useFormState();
  const {
    mutators: { push, remove },
  } = useForm();
  const { uploadApplication } = useUserQueries();
  const snackNotify = useNotify();

  const applicationForms: IFile[] =
    values?.[fieldsGroupName]?.applicationForms ?? [];

  const addNewFiles = useCallback(
    async (files) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadApplication(file);
          push(`${fieldsGroupName}.applicationForms`, data);
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);
          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [fieldsGroupName],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFileHandler = useCallback(
    (file) => {
      const index = applicationForms.indexOf(file);

      remove(`${fieldsGroupName}.applicationForms`, index);
    },
    [applicationForms, fieldsGroupName, remove],
  );

  return (
    <div className={classes.container}>
      <FieldsGroupLabel
        label="Application Files"
        className="fields-group-label"
      />
      <div className={classes.subContainer}>
        <UploadFile
          uploadedFiles={applicationForms}
          addNewFiles={addNewFiles}
          removeFile={removeFileHandler}
          infoText={
            "You can browse hard copy of application form or/and any docs required."
          }
          titleText={"Upload Application Form"}
          allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
          maxSize={30}
          maxFiles={4}
        />
      </div>
      <div className={classes.subContainer}>
        <Field
          fullWidth
          type="text"
          label="Application Link"
          placeholder="www.edu.uk"
          infoIcon={"You can put the link of application form"}
          component={BootstrapInput}
          name={`${fieldsGroupName}.applicationLink`}
        />
      </div>
    </div>
  );
};

export default ApplicationFiles;
